import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ApiMeta } from '../../../_core/_model/api-response';
import {
  AdditionalDocumentRequestListItemDto,
  AdditionalDocumentRequestQuery,
  CreateAdditionalDocumentRequestDto
} from '../../../_core/_model/additional-documents';
import { AdditionalDocumentsService } from '../../../_core/_services/additional-documents.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-additional-documents',
  standalone: false,
  templateUrl: './admin-additional-documents.html',
  styleUrl: './admin-additional-documents.css'
})
export class AdminAdditionalDocuments implements OnInit {
  loading = false;
  creating = false;
  showCreate = false;
  error: string | null = null;
  items: AdditionalDocumentRequestListItemDto[] = [];
  meta: ApiMeta | null = null;

  filters: AdditionalDocumentRequestQuery = {
    status: '',
    scopeType: '',
    userId: '',
    depositId: '',
    requestTypeCode: '',
    page: 1,
    rowsPerPage: 20
  };

  createModel: CreateAdditionalDocumentRequestDto = this.newRequest();

  constructor(
    private documents: AdditionalDocumentsService,
    private notify: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.load();
  }

  get headerPage(): string {
    return this.router.url.startsWith('/review/') ? 'Document Reviews' : 'Admin';
  }

  get basePath(): string {
    return this.router.url.startsWith('/review/')
      ? '/review/additional-documents'
      : '/admin/additional-documents';
  }

  load(page = 1): void {
    this.filters.page = page;
    this.loading = true;
    this.error = null;

    this.documents.getAdminRequests(this.filters)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: response => {
          this.items = response.data || [];
          this.meta = response.meta || null;
        },
        error: error => {
          this.items = [];
          this.meta = null;
          this.error = error?.error?.message || 'Unable to load document requests.';
          this.notify.showError(this.error!, 'Additional Documents');
        }
      });
  }

  clearFilters(): void {
    this.filters = { page: 1, rowsPerPage: 20, status: '', scopeType: '', userId: '', depositId: '', requestTypeCode: '' };
    this.load(1);
  }

  createRequest(): void {
    const model = this.createModel;
    if (!model.userId.trim() || !model.title.trim() || !model.instructions.trim()) {
      this.notify.showError('User ID, title and instructions are required.', 'Additional Documents');
      return;
    }
    if (model.scopeType === 'Deposit' && !model.depositId?.trim()) {
      this.notify.showError('Deposit ID is required for a deposit document request.', 'Additional Documents');
      return;
    }

    const payload: CreateAdditionalDocumentRequestDto = {
      ...model,
      userId: model.userId.trim(),
      depositId: model.scopeType === 'Deposit' ? model.depositId?.trim() || null : null,
      depositRiskReviewId: model.depositRiskReviewId?.trim() || null,
      requestTypeCode: model.requestTypeCode.trim(),
      documentTypeCode: model.documentTypeCode.trim(),
      title: model.title.trim(),
      instructions: model.instructions.trim(),
      reason: model.reason?.trim() || null,
      expiresAt: model.expiresAt ? new Date(model.expiresAt).toISOString() : null
    };

    this.creating = true;
    this.documents.createRequest(payload)
      .pipe(finalize(() => (this.creating = false)))
      .subscribe({
        next: response => {
          this.notify.showSuccess(response.message || 'Document request created.', 'Additional Documents');
          this.showCreate = false;
          this.createModel = this.newRequest();
          if (response.data?.id) this.router.navigate([this.basePath, response.data.id]);
          else this.load(1);
        },
        error: error => this.notify.showError(
          error?.error?.error?.details || error?.error?.message || 'Unable to create the request.',
          'Additional Documents'
        )
      });
  }

  open(item: AdditionalDocumentRequestListItemDto): void {
    this.router.navigate([this.basePath, item.id]);
  }

  statusClass(status: string): string {
    switch (status) {
      case 'Approved': return 'text-bg-success';
      case 'Requested':
      case 'AwaitingReplacement': return 'text-bg-danger';
      case 'Submitted':
      case 'UnderReview': return 'text-bg-warning';
      case 'Cancelled':
      case 'Expired': return 'text-bg-secondary';
      default: return 'text-bg-info';
    }
  }

  private newRequest(): CreateAdditionalDocumentRequestDto {
    return {
      userId: '',
      scopeType: 'Deposit',
      depositId: '',
      depositRiskReviewId: '',
      requestTypeCode: 'SOURCE_OF_FUNDS',
      documentTypeCode: 'SOURCE_OF_FUNDS',
      title: 'Source of funds document required',
      instructions: 'Upload a recent document that clearly shows the source of the funds used for this deposit. The document should show your name, relevant transaction details and date.',
      reason: '',
      isBlocking: true,
      expiresAt: null
    };
  }
}
